import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { FileSignature, Mail, FileText, Building2, Upload, Loader2, RefreshCw, CheckCircle2, AlertCircle, Eye, Trash2, Settings as SettingsIcon, Send, Sparkles } from 'lucide-react';

const STATUS_LABELS = {
  pending: { label: 'Da firmare', color: 'bg-amber-100 text-amber-800 border-amber-300' },
  signed: { label: 'Firmato', color: 'bg-blue-100 text-blue-800 border-blue-300' },
  invoiced: { label: 'Fatturato', color: 'bg-violet-100 text-violet-800 border-violet-300' },
  uploaded: { label: 'Caricato', color: 'bg-cyan-100 text-cyan-800 border-cyan-300' },
  completed: { label: 'Completato', color: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  error: { label: 'Errore', color: 'bg-red-100 text-red-800 border-red-300' },
};

export default function SignFlowPage() {
  const [tab, setTab] = useState('inbox');
  const [config, setConfig] = useState(null);
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [polling, setPolling] = useState(false);
  const [processingId, setProcessingId] = useState(null);
  const [imapForm, setImapForm] = useState({
    server: '', port: 993, use_ssl: true, email: '', password: '',
    folder: 'INBOX', sender_filter: '', subject_filter: '', max_age_days: 30,
  });
  const [arubaForm, setArubaForm] = useState({
    enabled: false, username: '', password: '',
    cliente_ragione_sociale: '', cliente_partita_iva: '', cliente_codice_sdi: '',
    cliente_indirizzo: '', iva_percent: 22.0, descrizione_template: 'Consulenza tecnica come da contratto del {data}',
  });
  const [uploadForm, setUploadForm] = useState({
    enabled: false, url: '', username: '', password: '', upload_folder: '', notes: '',
  });
  const [savingConfig, setSavingConfig] = useState(false);
  const [testingImap, setTestingImap] = useState(false);
  const [imapTestResult, setImapTestResult] = useState(null);

  useEffect(() => { loadConfig(); loadEmails(); }, []);

  const loadConfig = async () => {
    try {
      const r = await api.get('/signflow/config');
      setConfig(r.data);
      setImapForm(prev => ({ ...prev, ...r.data.imap, password: '' }));
      setArubaForm(prev => ({ ...prev, ...r.data.aruba, password: '' }));
      setUploadForm(prev => ({ ...prev, ...r.data.upload_site, password: '' }));
    } catch (e) { /* primo accesso */ }
  };

  const loadEmails = async () => {
    setLoading(true);
    try {
      const r = await api.get('/signflow/emails');
      setEmails(r.data || []);
    } catch (e) { toast.error('Errore caricamento email'); }
    finally { setLoading(false); }
  };

  const saveConfig = async () => {
    setSavingConfig(true);
    try {
      const payload = {
        imap: { ...imapForm, password: imapForm.password || null },
        aruba: { ...arubaForm, password: arubaForm.password || null },
        upload_site: { ...uploadForm, password: uploadForm.password || null },
      };
      await api.put('/signflow/config', payload);
      toast.success('Configurazione salvata (password cifrate AES)');
      setImapForm(f => ({ ...f, password: '' }));
      setArubaForm(f => ({ ...f, password: '' }));
      setUploadForm(f => ({ ...f, password: '' }));
      loadConfig();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore salvataggio'); }
    finally { setSavingConfig(false); }
  };

  const testImap = async () => {
    setTestingImap(true);
    setImapTestResult(null);
    try {
      const r = await api.post('/signflow/test-imap');
      setImapTestResult({ ok: true, folders: r.data.folders });
      toast.success(`Login OK — ${r.data.folders.length} cartelle visibili`);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      setImapTestResult({ ok: false, error: msg });
      toast.error('Test fallito: ' + msg);
    } finally { setTestingImap(false); }
  };

  const pollEmails = async () => {
    setPolling(true);
    try {
      const r = await api.post('/signflow/poll');
      toast.success(`${r.data.new_emails} nuove email • ${r.data.duplicates_skipped} duplicate skippate`);
      loadEmails();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore polling'); }
    finally { setPolling(false); }
  };

  const signEmail = async (id) => {
    setProcessingId(id);
    try {
      const r = await api.post(`/signflow/emails/${id}/sign`);
      toast.success(`Contratto firmato${r.data.signature_applied ? '' : ' (no firma in profilo)'}`);
      loadEmails();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore firma'); }
    finally { setProcessingId(null); }
  };

  const processEmail = async (id, dryRun) => {
    setProcessingId(id);
    try {
      const r = await api.post(`/signflow/emails/${id}/process?dry_run=${dryRun}`);
      const actions = r.data.actions || [];
      const msg = actions.map(a => `${a.step}: ${a.status}`).join(' | ');
      toast.success(`${dryRun ? 'DRY-RUN' : 'PROCESS'}: ${msg}`, { duration: 4000 });
      loadEmails();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore process'); }
    finally { setProcessingId(null); }
  };

  const deleteEmail = async (id) => {
    if (!window.confirm('Eliminare definitivamente questa email dal SignFlow?')) return;
    try {
      await api.delete(`/signflow/emails/${id}`);
      toast.success('Eliminata');
      loadEmails();
    } catch (e) { toast.error('Errore eliminazione'); }
  };

  return (
    <div className="space-y-6" data-testid="signflow-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <FileSignature className="w-7 h-7 text-violet-600" /> SignFlow
            <Badge variant="secondary" className="ml-2">v1.1.0 BETA</Badge>
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Automazione: email → conversione PDF → firma → (fattura Aruba) → upload sito destinazione.
          </p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="signflow-tabs">
          <TabsTrigger value="inbox" data-testid="tab-inbox"><Mail className="w-4 h-4 mr-1" /> Inbox Contratti</TabsTrigger>
          <TabsTrigger value="config" data-testid="tab-config"><SettingsIcon className="w-4 h-4 mr-1" /> Configurazione</TabsTrigger>
        </TabsList>

        {/* === INBOX === */}
        <TabsContent value="inbox" className="space-y-4">
          <div className="flex items-center gap-2">
            <Button onClick={pollEmails} disabled={polling} className="bg-violet-600 hover:bg-violet-700" data-testid="poll-btn">
              {polling ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Cerca nuove email contratti
            </Button>
            <Button onClick={loadEmails} variant="outline" disabled={loading} data-testid="reload-btn">
              <RefreshCw className="w-4 h-4" />
            </Button>
            <div className="text-sm text-slate-500 ml-auto">{emails.length} email totali</div>
          </div>

          {emails.length === 0 && !loading && (
            <Card className="bg-slate-50">
              <CardContent className="py-10 text-center text-slate-500">
                <Mail className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p className="font-medium">Nessuna email in coda</p>
                <p className="text-sm mt-1">Configura prima IMAP nella tab "Configurazione", poi clicca "Cerca nuove email".</p>
              </CardContent>
            </Card>
          )}

          <div className="space-y-3">
            {emails.map((em, idx) => {
              const st = STATUS_LABELS[em.status] || STATUS_LABELS.pending;
              const isProcessing = processingId === em.id;
              const ext = em.extracted_data || {};
              return (
                <motion.div
                  key={em.id}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.04 }}
                >
                  <Card data-testid={`email-card-${em.id}`} className="border-l-4 border-l-violet-400">
                    <CardContent className="py-3">
                      <div className="flex items-start gap-3 flex-wrap">
                        <div className="flex-1 min-w-[260px]">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge className={st.color} data-testid={`status-${em.id}`}>{st.label}</Badge>
                            <span className="font-semibold text-slate-800 text-sm">{em.subject || '(no subject)'}</span>
                          </div>
                          <p className="text-xs text-slate-500 mt-1"><strong>Da:</strong> {em.from || '?'}</p>
                          <p className="text-xs text-slate-500"><strong>Data:</strong> {em.email_date || '?'}</p>
                          <p className="text-xs text-slate-500 flex items-center gap-1">
                            <FileText className="w-3 h-3" /> {em.attachment_filename} ({(em.attachment_size / 1024).toFixed(0)} KB)
                          </p>

                          {ext && (ext.importo_netto != null || ext.cliente_ragione_sociale) && (
                            <div className="bg-violet-50 border border-violet-200 rounded p-2 mt-2 text-xs">
                              <p className="font-semibold text-violet-800 flex items-center gap-1">
                                <Sparkles className="w-3 h-3" /> AI ha estratto:
                              </p>
                              {ext.importo_netto != null && (
                                <p>💰 Importo: <strong>{ext.importo_netto}€</strong> + IVA {ext.iva_percentuale ?? 22}%</p>
                              )}
                              {ext.data_contratto && <p>📅 Data: {ext.data_contratto}</p>}
                              {ext.cliente_ragione_sociale && <p>🏢 Cliente: {ext.cliente_ragione_sociale}</p>}
                              {ext.descrizione && <p className="italic">"{ext.descrizione}"</p>}
                              {ext._ai_error && <p className="text-red-600">⚠️ AI: {ext._ai_error}</p>}
                            </div>
                          )}

                          {em.errors && em.errors.length > 0 && (
                            <div className="bg-red-50 border border-red-200 rounded p-2 mt-2 text-xs text-red-700">
                              {em.errors.map((e, i) => <p key={i}>⚠️ {e}</p>)}
                            </div>
                          )}
                        </div>

                        <div className="flex flex-col gap-1.5 min-w-[180px]">
                          {em.status === 'pending' && (
                            <Button
                              size="sm"
                              onClick={() => signEmail(em.id)}
                              disabled={isProcessing}
                              className="bg-blue-600 hover:bg-blue-700"
                              data-testid={`sign-btn-${em.id}`}
                            >
                              {isProcessing ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <FileSignature className="w-3 h-3 mr-1" />}
                              Converti & Firma
                            </Button>
                          )}
                          {em.status === 'signed' && (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/signflow/emails/${em.id}/signed-pdf`, '_blank')}
                                data-testid={`view-pdf-${em.id}`}
                              >
                                <Eye className="w-3 h-3 mr-1" /> Anteprima PDF
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => processEmail(em.id, true)}
                                disabled={isProcessing}
                                className="bg-amber-600 hover:bg-amber-700"
                                data-testid={`process-dry-${em.id}`}
                              >
                                {isProcessing ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
                                Simula Fattura+Upload (DRY)
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => processEmail(em.id, false)}
                                disabled={isProcessing}
                                className="bg-emerald-700 hover:bg-emerald-800"
                                data-testid={`process-real-${em.id}`}
                              >
                                <Send className="w-3 h-3 mr-1" /> Procedi REALE
                              </Button>
                            </>
                          )}
                          {em.status === 'completed' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/signflow/emails/${em.id}/signed-pdf`, '_blank')}
                              data-testid={`view-pdf-${em.id}`}
                            >
                              <Eye className="w-3 h-3 mr-1" /> PDF firmato
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => deleteEmail(em.id)}
                            className="text-red-600"
                            data-testid={`delete-btn-${em.id}`}
                          >
                            <Trash2 className="w-3 h-3 mr-1" /> Elimina
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </TabsContent>

        {/* === CONFIG === */}
        <TabsContent value="config" className="space-y-4">
          <ConfigSection
            title="IMAP — Casella Email"
            icon={<Mail className="w-5 h-5 text-blue-600" />}
            description="Connessione alla tua casella di posta per leggere i contratti in arrivo."
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Server IMAP</Label>
                <Input value={imapForm.server} onChange={e => setImapForm({ ...imapForm, server: e.target.value })} placeholder="imap.aruba.it / outlook.office365.com" data-testid="imap-server" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Porta</Label>
                  <Input type="number" value={imapForm.port} onChange={e => setImapForm({ ...imapForm, port: parseInt(e.target.value || '993', 10) })} data-testid="imap-port" />
                </div>
                <div className="flex items-end gap-2 pb-1">
                  <Checkbox checked={imapForm.use_ssl} onCheckedChange={(v) => setImapForm({ ...imapForm, use_ssl: !!v })} data-testid="imap-ssl" />
                  <Label className="cursor-pointer">SSL</Label>
                </div>
              </div>
              <div>
                <Label>Email</Label>
                <Input value={imapForm.email} onChange={e => setImapForm({ ...imapForm, email: e.target.value })} placeholder="tu@dominio.it" data-testid="imap-email" />
              </div>
              <div>
                <Label>Password App {config?.imap?.password_set && '(già salvata — lascia vuoto per non cambiare)'}</Label>
                <Input type="password" value={imapForm.password} onChange={e => setImapForm({ ...imapForm, password: e.target.value })} data-testid="imap-password" />
              </div>
              <div>
                <Label>Cartella</Label>
                <Input value={imapForm.folder} onChange={e => setImapForm({ ...imapForm, folder: e.target.value })} placeholder="INBOX o INBOX/Contratti" data-testid="imap-folder" />
              </div>
              <div>
                <Label>Max giorni indietro</Label>
                <Input type="number" value={imapForm.max_age_days} onChange={e => setImapForm({ ...imapForm, max_age_days: parseInt(e.target.value || '30', 10) })} data-testid="imap-max-days" />
              </div>
              <div>
                <Label>Filtro mittente (contiene)</Label>
                <Input value={imapForm.sender_filter} onChange={e => setImapForm({ ...imapForm, sender_filter: e.target.value })} placeholder="contratti@clientex.com" data-testid="imap-sender" />
              </div>
              <div>
                <Label>Filtro oggetto (contiene)</Label>
                <Input value={imapForm.subject_filter} onChange={e => setImapForm({ ...imapForm, subject_filter: e.target.value })} placeholder="contratto" data-testid="imap-subject" />
              </div>
            </div>
            <div className="flex gap-2 mt-3 items-center">
              <Button size="sm" variant="outline" onClick={testImap} disabled={testingImap || !config?.imap?.password_set && !imapForm.password} data-testid="imap-test-btn">
                {testingImap ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
                Test connessione
              </Button>
              {imapTestResult?.ok && (
                <span className="text-xs text-emerald-700">✓ {imapTestResult.folders.length} cartelle: {imapTestResult.folders.slice(0, 4).join(', ')}{imapTestResult.folders.length > 4 ? '…' : ''}</span>
              )}
              {imapTestResult?.ok === false && (
                <span className="text-xs text-red-700">✗ {imapTestResult.error}</span>
              )}
            </div>
          </ConfigSection>

          <ConfigSection
            title="Fatturazione Elettronica Aruba (STUB — pending credenziali reali)"
            icon={<Building2 className="w-5 h-5 text-violet-600" />}
            description="Dati per emissione fattura automatica. NOTA v1.1.0: lo step Aruba è simulato in DRY-RUN — verrà attivato il bot Playwright quando fornirai credenziali."
          >
            <div className="flex items-center gap-2 mb-2">
              <Checkbox checked={arubaForm.enabled} onCheckedChange={(v) => setArubaForm({ ...arubaForm, enabled: !!v })} data-testid="aruba-enabled" />
              <Label className="cursor-pointer font-semibold">Abilita step Aruba</Label>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><Label>Username Aruba</Label><Input value={arubaForm.username} onChange={e => setArubaForm({ ...arubaForm, username: e.target.value })} data-testid="aruba-username" /></div>
              <div><Label>Password Aruba {config?.aruba?.password_set && '(salvata)'}</Label><Input type="password" value={arubaForm.password} onChange={e => setArubaForm({ ...arubaForm, password: e.target.value })} data-testid="aruba-password" /></div>
              <div><Label>Cliente — Ragione Sociale</Label><Input value={arubaForm.cliente_ragione_sociale} onChange={e => setArubaForm({ ...arubaForm, cliente_ragione_sociale: e.target.value })} data-testid="aruba-cliente-rs" /></div>
              <div><Label>Cliente — P.IVA</Label><Input value={arubaForm.cliente_partita_iva} onChange={e => setArubaForm({ ...arubaForm, cliente_partita_iva: e.target.value })} data-testid="aruba-cliente-piva" /></div>
              <div><Label>Cliente — Codice SDI</Label><Input value={arubaForm.cliente_codice_sdi} onChange={e => setArubaForm({ ...arubaForm, cliente_codice_sdi: e.target.value })} placeholder="7 caratteri" data-testid="aruba-cliente-sdi" /></div>
              <div><Label>Cliente — Indirizzo</Label><Input value={arubaForm.cliente_indirizzo} onChange={e => setArubaForm({ ...arubaForm, cliente_indirizzo: e.target.value })} data-testid="aruba-cliente-indirizzo" /></div>
              <div><Label>IVA %</Label><Input type="number" step="0.01" value={arubaForm.iva_percent} onChange={e => setArubaForm({ ...arubaForm, iva_percent: parseFloat(e.target.value || '22') })} data-testid="aruba-iva" /></div>
              <div className="md:col-span-2"><Label>Template descrizione fattura</Label><Input value={arubaForm.descrizione_template} onChange={e => setArubaForm({ ...arubaForm, descrizione_template: e.target.value })} placeholder="Consulenza tecnica come da contratto del {data}" data-testid="aruba-descr" /></div>
            </div>
          </ConfigSection>

          <ConfigSection
            title="Sito Destinazione Upload (STUB — pending credenziali reali)"
            icon={<Upload className="w-5 h-5 text-cyan-600" />}
            description="Sito web dove ricaricare contratto firmato + fattura. NOTA v1.1.0: simulato in DRY-RUN — bot Playwright attivabile quando fornirai URL+credenziali."
          >
            <div className="flex items-center gap-2 mb-2">
              <Checkbox checked={uploadForm.enabled} onCheckedChange={(v) => setUploadForm({ ...uploadForm, enabled: !!v })} data-testid="upload-enabled" />
              <Label className="cursor-pointer font-semibold">Abilita step upload</Label>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2"><Label>URL del sito</Label><Input value={uploadForm.url} onChange={e => setUploadForm({ ...uploadForm, url: e.target.value })} placeholder="https://portale.clientex.com" data-testid="upload-url" /></div>
              <div><Label>Username</Label><Input value={uploadForm.username} onChange={e => setUploadForm({ ...uploadForm, username: e.target.value })} data-testid="upload-username" /></div>
              <div><Label>Password {config?.upload_site?.password_set && '(salvata)'}</Label><Input type="password" value={uploadForm.password} onChange={e => setUploadForm({ ...uploadForm, password: e.target.value })} data-testid="upload-password" /></div>
              <div className="md:col-span-2"><Label>Cartella di destinazione</Label><Input value={uploadForm.upload_folder} onChange={e => setUploadForm({ ...uploadForm, upload_folder: e.target.value })} placeholder="/Contratti firmati/2026" data-testid="upload-folder" /></div>
              <div className="md:col-span-2"><Label>Note</Label><Textarea rows={2} value={uploadForm.notes} onChange={e => setUploadForm({ ...uploadForm, notes: e.target.value })} placeholder="Es: dopo login, click su 'Carica documento'…" data-testid="upload-notes" /></div>
            </div>
          </ConfigSection>

          <div className="flex justify-end gap-2 sticky bottom-2 bg-white/95 backdrop-blur p-3 border-t">
            <Button onClick={saveConfig} disabled={savingConfig} className="bg-violet-700 hover:bg-violet-800" data-testid="save-config-btn">
              {savingConfig ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Salva configurazione
            </Button>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ConfigSection({ title, icon, description, children }) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">{icon} {title}</CardTitle>
        <p className="text-xs text-slate-500">{description}</p>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}
